import * as THREE from 'three';
import arena from './rooftopArena.json';
import { validateSurfaces, shapeOf } from '../core/surfaces.js';
import { validatePit } from '../core/pit.js';
import { createCrowdBodies } from './crowd.js';
import { arenaShape, buildLighting, createKit, drawnTexture, seeded, weatheredBrick } from './kit.js';

/**
 * A roof: the first arena open to the sky.
 *
 * Everything hard up here is the plant a roof carries — two HVAC units, a vent
 * stack, the stair bulkhead, a skylight — plus the brick flank of the taller
 * building next door and a fence along the far end. All of it is a `box`, a
 * `column` or a `plane` the collision already knew, and all of it stands
 * outside the walkable floor.
 *
 * **The signature is the parapet.** It is low, and on the far side of it is
 * the street. It is the subway's track pit turned on its side: a *region*
 * rather than a surface, tested before anything hard, so a fighter carried past
 * the line is going over rather than bouncing off a wall eighty centimetres
 * tall. Like the pit it is a finisher — anybody in danger who goes over is
 * gone — and a fighter who is not in danger catches the fire escape below the
 * lip and climbs back, which costs them more than any wall up here. It is
 * `sheer`, which is what tells a fighter with an eye for the room that this
 * drop is worth aiming at with no train in it.
 *
 * A body that goes over is carried up and across the parapet by the host and
 * then falls to the street, so the finish reads as one. The camera stays on
 * the roof.
 */

export { arena as ROOFTOP_ARENA };

const OVERHANG = 0.15;
const WALL_THICKNESS = 0.2;

export function createRooftopArena(config = arena, { onWarn, crowd = null } = {}) {
  try {
    validateSurfaces(config.surfaces);
    validatePit(config.pit);
  } catch (error) {
    onWarn?.(error.message);
  }

  const group = new THREE.Group();
  group.name = `arena-${config.id}`;
  const kit = createKit(group);
  const colors = config.palette ?? {};
  const color = (key, fallback) => new THREE.Color(colors[key] ?? fallback);

  // --- the sky --------------------------------------------------------------
  // A dome painted from the zenith down to a hot horizon, unlit and unfogged:
  // the stage's fog is the near-black of an underground room, and fogging the
  // sky would put the subway's ceiling back over an open roof.
  const skyGeometry = new THREE.SphereGeometry(90, 32, 16);
  const top = color('skyTop', '#16183a');
  const mid = color('skyMid', '#5a3a6a');
  const horizon = color('skyHorizon', '#f0905a');
  const skyColors = [];
  const at = new THREE.Color();
  const position = skyGeometry.getAttribute('position');
  for (let i = 0; i < position.count; i++) {
    const up = Math.max(0, position.getY(i) / 90);
    if (up < 0.18) at.copy(horizon).lerp(mid, up / 0.18);
    else at.copy(mid).lerp(top, Math.min(1, (up - 0.18) / 0.5));
    if (position.getY(i) < 0) at.copy(horizon).multiplyScalar(0.35);
    skyColors.push(at.r, at.g, at.b);
  }
  skyGeometry.setAttribute('color', new THREE.Float32BufferAttribute(skyColors, 3));
  const sky = kit.place(
    skyGeometry,
    kit.keep(new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.BackSide, fog: false, depthWrite: false })),
    { name: 'sky', casts: false, receives: false }
  );
  sky.renderOrder = -1;

  // --- the deck -------------------------------------------------------------
  const deck = config.deck;
  const deckWidth = deck.maxX - deck.minX;
  const deckDepth = deck.maxZ - deck.minZ;
  const gravel = drawnTexture(256, 256, (ctx, w, h, random) => {
    ctx.fillStyle = colors.deck ?? '#4b4643';
    ctx.fillRect(0, 0, w, h);
    // Tar and gravel: a scatter of lighter and darker chips.
    for (let i = 0; i < 2600; i++) {
      const shade = random() < 0.5 ? 'rgba(255,255,255,0.07)' : 'rgba(0,0,0,0.12)';
      ctx.fillStyle = shade;
      ctx.fillRect(random() * w, random() * h, 1 + random() * 2.5, 1 + random() * 2.5);
    }
    // Seams in the membrane, every couple of metres.
    ctx.strokeStyle = 'rgba(0,0,0,0.25)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(0, h - 1);
    ctx.lineTo(w, h - 1);
    ctx.stroke();
  }, { seed: 3, repeat: [deckWidth / 2.2, deckDepth / 2.2] });
  if (gravel) kit.keep(gravel);
  kit.box(
    deckWidth,
    deck.thickness,
    deckDepth,
    kit.material({ color: gravel ? 0xffffff : color('deck', '#4b4643'), map: gravel, roughness: 0.97, metalness: 0 }),
    {
      x: (deck.minX + deck.maxX) / 2,
      y: -deck.thickness / 2,
      z: (deck.minZ + deck.maxZ) / 2,
      name: 'roof-deck',
      casts: false,
    }
  );

  // --- the parapet, and the drop behind it ----------------------------------
  const parapet = config.parapet;
  const pit = config.pit;
  // Weathered: each brick its own shade, soot down from the coping, and a
  // roughness map from the same layout so the faces and the mortar catch the
  // string lights differently (step 98).
  const bricks = weatheredBrick({ brick: colors.brick, mortar: colors.mortar, seed: 11 });
  const brick = bricks.map ? kit.keep(bricks.map) : null;
  const brickRough = bricks.roughnessMap ? kit.keep(bricks.roughnessMap) : null;
  const parapetMesh = kit.box(
    deckWidth,
    parapet.height,
    parapet.thickness,
    kit.material({
      color: brick ? 0xffffff : color('parapet', '#8a6b5a'),
      map: brick ? repeatOf(kit, brick, deckWidth, parapet.height) : null,
      roughnessMap: brickRough ? repeatOf(kit, brickRough, deckWidth, parapet.height) : null,
      roughness: brickRough ? 1 : 0.9,
      metalness: 0,
    }),
    {
      x: (deck.minX + deck.maxX) / 2,
      y: parapet.height / 2,
      z: parapet.z - parapet.thickness / 2,
      name: 'parapet',
    }
  );
  // The coping stones along the top, a shade lighter, so the lip reads as a lip.
  kit.box(deckWidth + 0.04, parapet.cap, parapet.thickness + 0.08, kit.material({ color: color('cap', '#b7aca0'), roughness: 0.8 }), {
    x: (deck.minX + deck.maxX) / 2,
    y: parapet.height + parapet.cap / 2,
    z: parapet.z - parapet.thickness / 2,
    name: 'parapet-cap',
  });
  // The building's own face going down, so the drop has a side to it.
  const facadeDepth = Math.abs(pit.floor);
  kit.box(deckWidth, facadeDepth, 0.3, kit.material({ color: color('brick', '#7b4a3c').multiplyScalar(0.6), roughness: 1 }), {
    x: (deck.minX + deck.maxX) / 2,
    y: -facadeDepth / 2 - deck.thickness,
    z: parapet.z - parapet.thickness + 0.15,
    name: 'facade',
    casts: false,
  });
  // The fire escape a fighter who goes over and is not finished catches.
  const iron = kit.material({ color: '#2b2d31', roughness: 0.6, metalness: 0.7 });
  kit.box(3.2, 0.06, 0.9, iron, { x: 1.4, y: -1.7, z: parapet.z - parapet.thickness - 0.45, name: 'fire-escape' });
  kit.box(3.2, 0.05, 0.05, iron, { x: 1.4, y: -0.8, z: parapet.z - parapet.thickness - 0.88, name: 'fire-escape-rail' });

  // --- the city -------------------------------------------------------------
  group.add(buildSkyline(kit, config.skyline, colors));

  // --- the plant on the roof, built from the collision's own data ----------
  const built = [];
  for (const surface of config.surfaces) {
    built.push({ surface, mesh: buildSurface(kit, surface, colors, brick, brickRough) });
  }

  // --- festoon lights -------------------------------------------------------
  // Somebody strung bulbs across the roof for this. They are the two lamps in
  // the lighting data, made visible.
  const strings = config.strings;
  if (strings) {
    const bulbMaterial = kit.material({ color: color('bulb', '#ffd9a0'), emissive: color('bulb', '#ffd9a0'), emissiveIntensity: 2.2 });
    const wire = [];
    const bulbs = new THREE.InstancedMesh(kit.keep(new THREE.SphereGeometry(0.045, 8, 6)), bulbMaterial, strings.bulbs);
    bulbs.name = 'festoon';
    const m = new THREE.Matrix4();
    for (let i = 0; i <= strings.bulbs; i++) {
      const t = i / strings.bulbs;
      const p = new THREE.Vector3(
        strings.from.x + (strings.to.x - strings.from.x) * t,
        strings.from.y + (strings.to.y - strings.from.y) * t - Math.sin(Math.PI * t) * strings.sag,
        strings.from.z + (strings.to.z - strings.from.z) * t
      );
      wire.push(p);
      if (i < strings.bulbs) {
        m.makeTranslation(p.x, p.y - 0.06, p.z);
        bulbs.setMatrixAt(i, m);
      }
    }
    group.add(bulbs);
    const line = new THREE.Line(kit.keep(new THREE.BufferGeometry().setFromPoints(wire)), kit.keep(new THREE.LineBasicMaterial({ color: '#1a1a1a' })));
    line.name = 'festoon-wire';
    group.add(line);
  }

  // Everything here holds still: one draw call per material.
  kit.mergeStatic(group, { exclude: (mesh) => mesh.name === 'sky' || mesh.name === 'parapet' });

  group.add(buildLighting(config.lighting));

  // --- the crowd ------------------------------------------------------------
  const bodies =
    crowd && config.crowd
      ? createCrowdBodies(crowd, { config: config.crowd, surfaces: config.surfaces, palette: colors })
      : null;
  if (bodies) group.add(bodies.object3D);

  return arenaShape(config, {
    group,
    kit,
    extra: { crowd: bodies, walls: built, parapet: parapetMesh },
    update(dt) {
      bodies?.update(dt);
    },
    dispose() {
      bodies?.dispose();
    },
    /**
     * Going over. A fighter finished past the line is carried up and across
     * the parapet before the host lets them fall — without it they would drop
     * straight down through the brickwork they were supposed to go over.
     */
    onStruck(event) {
      if (!event.pit || !event.knockout || !event.at) return null;
      return {
        rest: { x: event.at.x, z: pit.maxZ - (pit.over ?? 0.75) },
        arc: pit.arc ?? 1,
        wait: 0,
      };
    },
  });
}

/** Repeats a texture over a face of `width` x `height` metres, at one repeat per metre. */
function repeatOf(kit, texture, width, height) {
  const clone = kit.keep(texture.clone());
  clone.needsUpdate = true;
  // The brick is drawn two metres square.
  clone.repeat.set(width / 2, height / 2);
  return clone;
}

/** One piece of rooftop plant, by its `look`, standing where its surface says. */
function buildSurface(kit, surface, colors, brick, brickRough) {
  const height = surface.height ?? 1;
  const tone = (key, fallback) => new THREE.Color(colors[key] ?? fallback);

  if (shapeOf(surface) === 'column') {
    const metal = kit.material({ color: tone('vent', '#8d9297'), roughness: 0.45, metalness: 0.7 });
    const mesh = kit.cylinder(surface.radius, height, metal, {
      x: surface.center.x,
      y: height / 2,
      z: surface.center.z,
      name: `vent-${surface.id}`,
    });
    // A rain cap on the top of the stack.
    kit.cylinder(surface.radius * 1.7, 0.12, metal, {
      x: surface.center.x,
      y: height + 0.18,
      z: surface.center.z,
      top: 0.02,
      name: `vent-cap-${surface.id}`,
    });
    return mesh;
  }

  if (shapeOf(surface) === 'box') {
    const { center, halfX, halfZ } = surface;
    const width = halfX * 2;
    const depth = halfZ * 2;
    if (surface.look === 'hvac') {
      const casing = kit.material({ color: tone('hvac', '#9ea3a6'), roughness: 0.5, metalness: 0.55 });
      const dark = kit.material({ color: tone('hvacDark', '#3b3f43'), roughness: 0.7, metalness: 0.4 });
      const mesh = kit.box(width, height, depth, casing, { x: center.x, y: height / 2, z: center.z, name: `hvac-${surface.id}` });
      // The fan grille on top, and louvres down the side facing the fight.
      kit.cylinder(Math.min(halfX, halfZ) * 0.7, 0.05, dark, { x: center.x, y: height + 0.02, z: center.z, segments: 20, name: `hvac-fan-${surface.id}` });
      const facing = Math.sign(-center.x) || 1;
      for (let i = 0; i < 6; i++) {
        kit.box(0.02, height * 0.08, depth * 0.8, dark, {
          x: center.x + facing * (halfX + 0.005),
          y: height * (0.2 + i * 0.12),
          z: center.z,
          name: `hvac-louvre-${surface.id}-${i}`,
          casts: false,
        });
      }
      return mesh;
    }
    if (surface.look === 'bulkhead') {
      const walls = kit.material({ color: tone('bulkhead', '#6e665f'), roughness: 0.92 });
      const mesh = kit.box(width, height, depth, walls, { x: center.x, y: height / 2, z: center.z, name: `bulkhead-${surface.id}` });
      kit.box(0.04, 2.0, 0.9, kit.material({ color: tone('door', '#3d4a55'), roughness: 0.5, metalness: 0.5 }), {
        x: center.x - halfX - 0.015,
        y: 1.0,
        z: center.z,
        name: 'bulkhead-door',
      });
      // A caged bulb over the door.
      kit.box(0.08, 0.08, 0.08, kit.material({ color: '#ffe0b0', emissive: '#ffe0b0', emissiveIntensity: 2 }), {
        x: center.x - halfX - 0.06,
        y: 2.2,
        z: center.z,
        name: 'bulkhead-lamp',
        casts: false,
      });
      return mesh;
    }
    if (surface.look === 'skylight') {
      const frame = kit.material({ color: '#4d5257', roughness: 0.6, metalness: 0.6 });
      const mesh = kit.box(width, height * 0.6, depth, frame, { x: center.x, y: height * 0.3, z: center.z, name: `skylight-${surface.id}` });
      // A shallow glass pyramid on the curb.
      const glass = kit.material({ color: tone('skylight', '#9fc3d6'), roughness: 0.1, metalness: 0.3, transparent: true, opacity: 0.55 });
      const roof = kit.place(new THREE.ConeGeometry(Math.hypot(halfX, halfZ), height * 0.4, 4, 1), glass, {
        x: center.x,
        y: height * 0.6 + height * 0.2,
        z: center.z,
        rotY: Math.PI / 4,
        name: `skylight-glass-${surface.id}`,
      });
      roof.scale.set(halfX / Math.hypot(halfX, halfZ) * Math.SQRT2, 1, halfZ / Math.hypot(halfX, halfZ) * Math.SQRT2);
      return mesh;
    }
    return kit.box(width, height, depth, kit.material({ color: '#777' }), { x: center.x, y: height / 2, z: center.z, name: `box-${surface.id}` });
  }

  // A plane: the neighbour's brick, or the fence.
  const { from, to, normal } = surface;
  const span = Math.hypot(to.x - from.x, to.z - from.z) + OVERHANG * 2;
  const mid = { x: (from.x + to.x) / 2, z: (from.z + to.z) / 2 };
  const rotY = Math.atan2(-normal.x, -normal.z);
  if (surface.look === 'fence') {
    const fence = drawnTexture(64, 64, (ctx, w, h) => {
      ctx.clearRect(0, 0, w, h);
      ctx.strokeStyle = colors.fence ?? '#8e959b';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(w, h);
      ctx.moveTo(w, 0);
      ctx.lineTo(0, h);
      ctx.stroke();
    });
    if (fence) {
      kit.keep(fence);
      fence.repeat.set(span * 6, height * 6);
    }
    const mesh = kit.box(span, height, 0.02, kit.material({
      color: fence ? 0xffffff : tone('fence', '#8e959b'),
      map: fence,
      transparent: Boolean(fence),
      alphaTest: 0.3,
      side: THREE.DoubleSide,
      roughness: 0.5,
      metalness: 0.6,
    }), { x: mid.x - normal.x * 0.01, y: height / 2, z: mid.z - normal.z * 0.01, rotY, name: `fence-${surface.id}` });
    // Posts every two metres and a top rail, so the mesh hangs off something.
    const post = kit.material({ color: '#6c7277', roughness: 0.5, metalness: 0.7 });
    const along = { x: (to.x - from.x) / (span - OVERHANG * 2), z: (to.z - from.z) / (span - OVERHANG * 2) };
    for (let d = 0; d <= span - OVERHANG * 2 + 1e-6; d += 2) {
      kit.cylinder(0.035, height, post, { x: from.x + along.x * d, y: height / 2, z: from.z + along.z * d, segments: 8, name: `fence-post-${d}` });
    }
    return mesh;
  }

  return kit.box(span, height, WALL_THICKNESS, kit.material({
    color: brick ? 0xffffff : tone('brick', '#7b4a3c'),
    map: brick ? repeatOf(kit, brick, span, height) : null,
    roughnessMap: brickRough ? repeatOf(kit, brickRough, span, height) : null,
    roughness: 0.92,
  }), {
    x: mid.x - normal.x * (WALL_THICKNESS / 2),
    y: height / 2,
    z: mid.z - normal.z * (WALL_THICKNESS / 2),
    rotY,
    name: `wall-${surface.id}`,
  });
}

/**
 * The city past the parapet: one instanced box, one lit-window texture, one
 * draw call. Deterministic, so the same towers stand in the same places.
 */
function buildSkyline(kit, skyline, colors) {
  const group = new THREE.Group();
  group.name = 'skyline';
  if (!skyline) return group;
  const random = seeded(skyline.seed ?? 7);
  const windows = drawnTexture(64, 128, (ctx, w, h, rnd) => {
    ctx.fillStyle = '#000';
    ctx.fillRect(0, 0, w, h);
    for (let y = 4; y < h; y += 8) {
      for (let x = 3; x < w; x += 8) {
        if (rnd() < 0.34) {
          ctx.fillStyle = rnd() < 0.8 ? colors.window ?? '#ffcf8a' : '#a8c8ff';
          ctx.fillRect(x, y, 4, 4);
        }
      }
    }
  }, { seed: 5 });
  if (windows) {
    kit.keep(windows);
    windows.repeat.set(1, 2);
  }
  const material = kit.material({
    color: new THREE.Color(colors.building ?? '#14151c'),
    emissive: windows ? new THREE.Color('#ffffff') : new THREE.Color('#000000'),
    emissiveMap: windows,
    emissiveIntensity: windows ? 0.9 : 0,
    roughness: 1,
  });
  const towers = new THREE.InstancedMesh(kit.keep(new THREE.BoxGeometry(1, 1, 1)), material, skyline.count);
  towers.name = 'skyline-towers';
  const m = new THREE.Matrix4();
  const q = new THREE.Quaternion();
  for (let i = 0; i < skyline.count; i++) {
    const width = 4 + random() * 7;
    const depth = 4 + random() * 6;
    const height = skyline.minHeight + random() * (skyline.maxHeight - skyline.minHeight);
    const x = (random() - 0.5) * skyline.spread * 2;
    const z = skyline.near + (skyline.far - skyline.near) * random();
    m.compose(new THREE.Vector3(x, skyline.street + height / 2, z), q, new THREE.Vector3(width, height, depth));
    towers.setMatrixAt(i, m);
  }
  group.add(towers);
  return group;
}
